import { ContentSwitcher, IconButton, toast } from "@carbon6/c6-frontend-design";
import { useMemo, useState, type ReactNode } from "react";
import type { TrainingEvalVideoView } from "@/api/trainingEval";
import { DownloadIcon } from "@/components/icons";
import { EmptyState } from "@/shared/ui/emptyState/public";
import {
  MultiChannelVideoPlayer,
  type VideoChannelSource,
} from "@/components/video/MultiChannelVideoPlayer";
import { downloadCrossOriginFile } from "@/utils";
import {
  episodeTitle,
  isFailureEpisode,
  isSuccessEpisode,
  playableCameras,
  recordedVideoFilename,
  useRecordedEpisodeGallery,
  type PlayableCamera,
  type RecordedEpisode,
  type RecordedEpisodeFilter,
} from "@/modules/evaluation/public";
import styles from "./EvalRunRecordedVideos.module.css";

export function EvalRunRecordedVideos({
  videos,
}: {
  videos: readonly TrainingEvalVideoView[];
}): ReactNode {
  const gallery = useRecordedEpisodeGallery(videos);

  return (
    <section className={styles.section}>
      <h3 className={styles.heading}>녹화 영상</h3>
      {gallery.episodes.length === 0 ? (
        <EmptyState size="sm">이 Evaluation에서 녹화된 영상이 없습니다.</EmptyState>
      ) : (
        <div className={styles.panel} data-testid="recorded-videos-panel">
          <div className={styles.toolbar}>
            <div className={styles.controls}>
              <IconButton
                text="이전"
                aria-label="이전 영상"
                size="sm"
                variant="surface"
                disabled={gallery.page === 0}
                onClick={gallery.goToPreviousEpisode}
              />
              <span className={styles.mono}>
                {Math.min(gallery.page + 1, gallery.episodeCount)} / {gallery.episodeCount}
              </span>
              <IconButton
                text="다음"
                aria-label="다음 영상"
                size="sm"
                variant="surface"
                disabled={gallery.page >= gallery.episodeCount - 1}
                onClick={gallery.goToNextEpisode}
              />
              <ContentSwitcher
                label="영상 Filter"
                size="sm"
                value={gallery.activeFilter}
                onValueChange={(value) => gallery.selectFilter(value as RecordedEpisodeFilter)}
                options={[
                  { value: "all", label: `전체 (${gallery.counts.all})` },
                  { value: "failures", label: `실패 (${gallery.counts.failures})` },
                  { value: "successes", label: `성공 (${gallery.counts.successes})` },
                ]}
              />
            </div>
            {gallery.episode !== null ? (
              <div className={styles.episodeSide}>
                <EpisodeMeta episode={gallery.episode} />
                {/* 진행 중 잠금은 그 에피소드에만 적용한다. 다음 에피소드로 넘어가면 잠금을 새로 시작한다. */}
                <EpisodeDownloads key={gallery.episode.key} episode={gallery.episode} />
              </div>
            ) : null}
          </div>
          {gallery.episode !== null ? (
            <EpisodePlayer key={gallery.episode.key} episode={gallery.episode} />
          ) : (
            // 패널이 이미 sunken 면이다. 그래서 면을 다시 깔지 않는다.
            <EmptyState size="sm" mode="plain">
              선택한 Filter 에 맞는 영상이 없습니다.
            </EmptyState>
          )}
        </div>
      )}
    </section>
  );
}

function EpisodeMeta({ episode }: { episode: RecordedEpisode }): ReactNode {
  const failure = isFailureEpisode(episode);
  const success = isSuccessEpisode(episode);
  return (
    <div className={styles.meta}>
      <span className={styles.mono}>{episodeTitle(episode)}</span>
      <span
        className={`${styles.badge} ${
          failure ? styles.failure : success ? styles.success : styles.unknown
        }`}
      >
        {failure ? "실패" : success ? "성공" : "알 수 없음"}
      </span>
      {episode.outcome !== null ? <span className={styles.muted}>{episode.outcome}</span> : null}
      {episode.failureReasons.map((reason) => (
        <span key={reason} className={styles.reason}>
          {reason}
        </span>
      ))}
    </div>
  );
}

// 버튼 하나가 다운로드할 묶음이다. 카메라 한 대이거나 이 에피소드의 모든 카메라다.
type DownloadAction = {
  key: string;
  text: string;
  label: string;
  targets: readonly PlayableCamera[];
};

// 지금 보고 있는 에피소드의 영상을 다운로드한다. 목록 응답에 signed URL 이 포함되어 있어 추가
// 요청이 없다. 카메라가 여러 대이면 한 번에 다운로드하는 버튼을 추가한다.
function EpisodeDownloads({ episode }: { episode: RecordedEpisode }): ReactNode {
  const actions = useMemo<DownloadAction[]>(() => {
    const cameras = playableCameras(episode);
    const title = episodeTitle(episode);
    const perCamera = cameras.map((camera) => ({
      key: camera.id,
      text: camera.label,
      label: `${title} 의 ${camera.label} 영상 받기`,
      targets: [camera],
    }));
    if (cameras.length < 2) {
      return perCamera;
    }
    return [
      ...perCamera,
      {
        key: "all",
        text: `전체 (${cameras.length})`,
        label: `${title} 의 영상 ${cameras.length}개 모두 받기`,
        targets: cameras,
      },
    ];
  }, [episode]);
  const [busy, setBusy] = useState(false);

  async function save(targets: readonly PlayableCamera[]): Promise<void> {
    setBusy(true);
    let opened = 0;
    try {
      // 한 개씩 순서대로 다운로드한다. 동시에 요청하면 브라우저의 연속 다운로드 처리(확인 프롬프트,
      // 동시 요청 제한)에 막힌다. 몇 MB 크기 파일 몇 개에서는 동시 요청으로 줄이는 시간이 의미 없다.
      for (const target of targets) {
        const result = await downloadCrossOriginFile(
          target.url,
          recordedVideoFilename(episode, target.path),
        );
        if (result === "opened") opened += 1;
      }
    } finally {
      setBusy(false);
    }
    // 파일마다 알리면 알림이 너무 많아진다. 폴백이 있었다는 사실만 한 번 알린다.
    if (opened > 0) {
      toast.info(
        "바로 저장할 수 없어 영상을 새 탭에서 열었습니다. 보이지 않으면 pop-up 차단을 해제해 주세요.",
      );
    }
  }

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className={styles.downloads}>
      {actions.map((action) => (
        <IconButton
          key={action.key}
          text={action.text}
          aria-label={action.label}
          size="sm"
          variant="surface"
          disabled={busy}
          onClick={() => void save(action.targets)}
        >
          <DownloadIcon />
        </IconButton>
      ))}
    </div>
  );
}

function EpisodePlayer({ episode }: { episode: RecordedEpisode }): ReactNode {
  const channels = useMemo<VideoChannelSource[]>(
    () =>
      playableCameras(episode).map((camera) => ({
        id: camera.id,
        src: camera.url,
        label: camera.label,
        subLabel: camera.path,
      })),
    [episode],
  );
  return (
    <div className={channels.length === 1 ? styles.singleVideo : undefined}>
      <MultiChannelVideoPlayer
        channels={channels}
        emptyMessage="재생할 수 있는 camera 영상이 없습니다."
      />
    </div>
  );
}
